import { IPortfolioModelHistoric } from "../../../../services/PortfolioModelWalletService";
import { ICompositionDateValidation } from "../Interfaces/SummaryPortfolioInterfaces";

/**
 * Convierte una fecha en formato dd/MM/yyyy a un objeto Date
 * @param dateString Fecha en formato dd/MM/yyyy
 * @returns Objeto Date o null si la fecha es inválida
 */
const parseDate = (dateString: string): Date | null =>
{
    try
    {
        // Detectar formato: si contiene '/', es dd/MM/yyyy; si contiene '-', es YYYY-MM-DD
        if(dateString.includes('/'))
        {
            // Formato dd/MM/yyyy
            const [day, month, year] = dateString.split('/');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        else if(dateString.includes('-'))
        {
            // Formato YYYY-MM-DD
            const [year, month, day] = dateString.split('-');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        else
        {
            console.error('Formato de fecha no reconocido:', dateString);
            return null;
        }
    }
    catch(error)
    {
        console.error('Error parsing date:', dateString, error);
        return null;
    }
};

/**
 * Convierte una fecha a formato dd/MM/yyyy
 * @param date Objeto Date
 * @returns Fecha en formato dd/MM/yyyy
 */
const formatDate = (date: Date): string =>
{
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * Obtiene la fecha de ayer en formato dd/MM/yyyy
 * @returns Fecha de ayer en formato dd/MM/yyyy
 */
const getYesterdayFormatted = (): string =>
{
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return formatDate(yesterday);
};

/**
 * Obtiene la última composición histórica (excluyendo la virtual)
 * @param compositions Array de composiciones
 * @returns La última composición histórica o null si no hay ninguna
 */
const getLastHistoricalComposition = (compositions: IPortfolioModelHistoric[]): IPortfolioModelHistoric | null =>
{
    // Filtrar composiciones históricas (excluir la virtual con compositionId: -1)
    const historicalCompositions = compositions.filter(comp => comp.compositionId !== -1);

    if(historicalCompositions.length === 0)
    {
        return null;
    }

    // Ordenar por fecha descendente y tomar la primera
    const sortedCompositions = historicalCompositions.sort((a, b) =>
    {
        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);

        if (!dateA || !dateB) return 0;

        return dateB.getTime() - dateA.getTime();
    });

    return sortedCompositions[0];
};

/**
 * Valida si una fecha está en el rango válido para modificar la Cartera Modelo
 * Rango: (fecha_ultima_composicion, fecha_ayer) - SIN incluir límites
 * @param newDate Fecha de la nueva composición en formato dd/MM/yyyy
 * @param compositions Array de composiciones existentes
 * @returns Información de validación
 */
export const validateCompositionDateRange = (newDate: string, compositions: IPortfolioModelHistoric[]): ICompositionDateValidation =>
{
    // Obtener la última composición histórica
    const lastComposition = getLastHistoricalComposition(compositions);

    if(!lastComposition)
    {
        return{
            isInRange: false,
            lastCompositionDate: null,
            selectedCompositionDate: newDate,
            canModifyPortfolioModel: false
        };
    }

    const lastCompositionDate = lastComposition.date;
    const yesterdayFormatted = getYesterdayFormatted();

    // Parsear fechas
    const newDateParsed = parseDate(newDate);
    const lastDateParsed = parseDate(lastCompositionDate);
    const yesterdayParsed = parseDate(yesterdayFormatted);

    if(!newDateParsed || !lastDateParsed || !yesterdayParsed)
    {
        return{
            isInRange: false,
            lastCompositionDate,
            selectedCompositionDate: newDate,
            canModifyPortfolioModel: false
        };
    }

    // Validar rango: lastCompositionDate < newDate <= yesterday (exclusivo en última composición, inclusivo en ayer)
    const isInRange = lastDateParsed.getTime() < newDateParsed.getTime() && newDateParsed.getTime() <= yesterdayParsed.getTime();

    return{
        isInRange,
        lastCompositionDate,
        selectedCompositionDate: newDate,
        canModifyPortfolioModel: isInRange
    };
};

/**
 * Valida múltiples fechas de composiciones importadas
 * @param dates Array de fechas en formato dd/MM/yyyy
 * @param compositions Array de composiciones existentes
 * @returns Información de validación para la fecha más reciente en rango
 */
export const validateImportedCompositionDates = (dates: string[], compositions: IPortfolioModelHistoric[]): ICompositionDateValidation =>
{
    // Filtrar fechas que están en rango válido
    const validDates = dates.filter(date =>
    {
        const validation = validateCompositionDateRange(date, compositions);
        return validation.isInRange;
    });

    if(validDates.length === 0)
    {
        return{
            isInRange: false,
            lastCompositionDate: null,
            selectedCompositionDate: dates[0] || '',
            canModifyPortfolioModel: false
        };
    }

    // Encontrar la fecha más reciente entre las válidas
    const mostRecentValidDate = validDates.reduce((latest, current) =>
    {
        const latestParsed = parseDate(latest);
        const currentParsed = parseDate(current);

        if (!latestParsed || !currentParsed) return latest;

        return currentParsed.getTime() > latestParsed.getTime() ? current : latest;
    });

    // Validar la fecha más reciente
    return validateCompositionDateRange(mostRecentValidDate, compositions);
};